import { ConfigServices, Services } from '../services';
import { action, computed, observable, reaction, extras, toJS } from 'mobx';
import { theme } from '../Components/theme';
import { mergeAppStylesWithTheme } from '@kurtosys/ksys-portal-components/build/utils/mergeAppStylesWithTheme';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import { getParams, getFilterParams } from '../utils';
import getSortableColumn from '../utils/getSortableColumn';
import isExistingAdvancedSort from '../utils/isExistingAdvancedSort';
import * as FileSaver from 'file-saver';
import { getExtension } from 'mime/lite';
import { ConfigStore } from './ConfigStore';
import { DocumentStore } from './DocumentStore';
import { DocumentModuleStore } from './DocumentModuleStore';
import { ColumnStore } from './ColumnStore';
import {
    EXPANDED_PARAM,
    EXPAND_ALL_PARAM,
    DISALLOWED_FILTER_PARAMS,
    SORT_DIRECTIONS,
    ADVANCED_SORT_TYPES,
} from '../constants';
import { Config, FundCardRowConfig } from '../models/config';
import { Params } from '../models/params';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { AppParams } from '../models/configuration/AppParams';
import { LoadingTarget } from '../models/loading';
import { CsvHelper } from '../utils/CsvHelper';
import { translate } from '../Components/Translate';
import formatter, { padStart } from '../utils/formatter';
import { ExplorerDownloadDisclaimerSection } from '../models/configuration/explorer/ExplorerDownloadDisclaimerSection';
import { isNullOrUndefined, isArray } from 'util';
import { ExplorerWatchlist } from '../models/configuration/explorer/ExplorerWatchlist';
import { FilterSearchKeys } from '../models/components/Filter';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { LinkFilter } from '../models/configuration/LinkFilter';
import { SegmentationFilter } from '../models/configuration/SegmentationFilter';
import { FundDetailLists } from '../models/configuration/FundDetailLists';
import { utils } from '@kurtosys/ksys-app-template';
import { IEntityEntity, IEntity } from '../models/commonTypes';
import { ExpandStatus } from '../models/expandStatus';
import { BreakpointSize } from '../types';

// If a parent website is using mobx, a warning is logged that multiple mobx instances are active, which "might lead to unexpected results".
// Calling isolateGlobalState signals mobx that it is the intention that multiple mobx instances are active,
// without tracking each other, and will suppress the warning.
// See https://github.com/mobxjs/mobx/issues/1082
extras.isolateGlobalState();

export class DataStore extends BaseStore {
    @computed
    get fundTypeSearch() {
        if (this.config.fundTypeProperty) {
            return [
                {
                    property: this.config.fundTypeProperty,
                    values: [this.config.selectedFundType.fundType],
                    matchtype: 'MATCH',
                },
            ];
        }

        return [];
    }

    @computed
    get switchToCardView() {
        const configOption =
            this.config &&
            this.config.responsiveConfig &&
            this.config.responsiveConfig.switchToCardView;
        return configOption || 'sm';
    }

    @computed
    get processedFunds() {
        const processedFunds = this.funds.map(fund => {
            const { filteredDocuments } = new DocumentStore(
                this.stores,
                this.config,
                fund,
                this.globalDocuments
            );

            return {
                ...fund,
                documents: filteredDocuments,
            };
        });

        return processedFunds;
    }

    @computed
    get groupedFunds() {
        const groupedFunds = groupBy(
            this.processedFunds,
            `properties_pub.${this.config.fundGroupProperty}.value`
        );
        return observable(
            Object.keys(groupedFunds).map(fundGroupName => ({
                fundName: fundGroupName,
                grouped: this.config.fundGroupProperty ? true : false,
                shareClasses: groupedFunds[fundGroupName],
                columns: this.columnStore.columns,
            }))
        );
    }

    @computed
    get culture() {
        return this.defaultCulture || this.config.defaultCulture;
    }

    // Watchlist

    @computed
    get watchListKey() {
        let key = 'ksys_watchlist';
        if (this.config.applicationName) {
            key += `_${this.config.applicationName}`;
        }
        if (
            this.config.selectedFundType &&
            !this.config.selectedFundType.watchlist
        ) {
            const fundTypeLabel = this.config.selectedFundType.label;
            key += `_${fundTypeLabel}`;
        } else if (
            this.config.selectedFundType &&
            this.config.selectedFundType.watchlist &&
            this.config.selectedFundType.watchlist.scope === 'unique'
        ) {
            const fundTypeLabel =
                this.config.selectedFundType.watchlist.key ||
                this.config.selectedFundType.label;
            key += `_${fundTypeLabel}`;
            if (
                this.config.selectedModule &&
                this.config.selectedModule.config &&
                this.config.selectedModule.config.explorer &&
                this.config.selectedModule.config.explorer.watchlist &&
                // tslint:disable-next-line: prettier
                this.config.selectedModule.config.explorer.watchlist.scope ===
                    'unique'
            ) {
                const moduleLabel =
                    this.config.selectedModule.config.explorer.watchlist.key ||
                    this.config.selectedModule.label;
                key += `_${moduleLabel}`;
            }
        }
        return key;
    }

    @computed
    get watchListEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.config.watchListEnabled;
        const property =
            (this.watchListConfig && this.watchListConfig.property) ||
            this.config.watchListProperty;

        return !!(enabled && property);
    }

    @computed
    get watchListSearchEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.config.watchListEnabled;
        return !!(enabled && this.watchListActive);
    }

    @computed
    get watchListCount() {
        return this.watchList.length;
    }

    @computed
    get watchListSearch() {
        return [
            {
                property: this.config.watchListProperty,
                values: this.watchList,
                matchtype: 'MATCH',
            },
        ];
    }

    @computed
    get watchListConfig(): ExplorerWatchlist {
        return (
            this.config &&
            this.config.explorer &&
            this.config.explorer.watchlist
        );
    }

    // Identifier search config used in entity searches
    // This is derived from merging config.identifierSearch with a search term.
    // We use the property used for grouping funds as the default property to search on. Note that this may be overridden by identifierSearch config.
    @computed
    get identifierSearch() {
        return {
            properties: [this.config.fundGroupProperty || ''],
            fuzzy: false,
            ...this.config.identifierSearch,
            ...{ value: this.identifierSearchValue },
        };
    }

    @computed
    get searchValues() {
        return {
            properties: this.identifierSearchProperties,
            placeholder: this.identifierSearchPlaceholder,
            value: this.identifierSearchValue,
        };
    }

    @computed
    get identifierSearchProperties() {
        const aliases = this.identifierSearchAliases;
        const { identifierSearch } = this.config;

        if (!identifierSearch || !identifierSearch.properties) {
            return [];
        }

        const properties = identifierSearch.properties.map(code => {
            let name = aliases[code] || this.getClassPropertyLabel(code);
            name = translate(this.translations, name);
            return name;
        });
        return properties;
    }

    @computed
    get identifierSearchAliases() {
        const placeholder = this.config.identifierSearch.placeholder;
        if (placeholder && placeholder.aliases) {
            const aliases = {};
            for (const alias of placeholder.aliases) {
                aliases[alias.key] = alias.value;
            }
            return aliases;
        }
        return {};
    }

    @computed
    get identifierSearchPlaceholder() {
        const { placeholder } = this.config.identifierSearch;
        if (placeholder) {
            const { badge } = placeholder;
            return {
                format: placeholder.format || {
                    singular: 'Search {total} share class',
                    plural: 'Search {total} share classes',
                },
                badge: badge && {
                    show: badge.show && badge.format,
                    format: badge.format,
                    align: badge.align || 'right',
                },
            };
        } else {
            return {
                format: {
                    singular: 'Search {total} share class',
                    plural: 'Search {total} share classes',
                },
            };
        }
    }

    // Documents join spec used in entity searches
    // Returns config.documentsJoinSpec combined with documentSearch and documentSearchBySegmentation to limit the documents displayed.
    @computed
    get documentsJoinSpec() {
        const joinSpecSearch = this.config.documentSearch
            .concat(this.config.documentSearchBySegmentation.slice())
            .reduce((accumulator, currentValue) => {
                const existingPropertySearch = accumulator.find(
                    a => a.property === currentValue.property
                );

                if (existingPropertySearch) {
                    existingPropertySearch.values = existingPropertySearch.values.concat(
                        currentValue.values
                    );
                    return accumulator;
                }

                accumulator.push(currentValue);

                return accumulator;
            }, []);

        return this.config.documentsJoinSpec.map(joinSpec => {
            joinSpec.search = joinSpecSearch;
            return joinSpec;
        });
    }

    // Get filter properties that apply to entity search from filters config and query string filter params
    @computed
    get filterProperties() {
        return this.config.filtersConfig
            .filter(filter => filter.type === 'CLSS')
            .map(filter => filter.property)
            .concat(Object.keys(this.filterParams))
            .filter(property => !DISALLOWED_FILTER_PARAMS.includes(property));
    }

    // Build a search for fund/getFilters based on allowedValues specified in filter config
    @computed
    get filterPropertiesAllowedValuesSearch() {
        return this.config.filtersConfig
            .filter(filter => filter.type === 'CLSS')
            .map(filter => {
                if (filter.allowedValues && filter.allowedValues.length > 0) {
                    return {
                        property: filter.property,
                        values: filter.allowedValues || [],
                        matchtype: 'MATCH',
                    };
                }
            })
            .filter(filter => filter !== undefined);
    }

    // Get filter properties that apply to document meta from filters config
    @computed
    get documentFilterCodes() {
        return this.config.filtersConfig
            .filter(filter => filter.type === 'DOCUMENT_META')
            .map(filter => filter.property);
    }

    // Build a search for documents/getFilters based on allowedValues specified in filter config
    @computed
    get documentFilterAllowedValuesSearch() {
        return this.config.filtersConfig
            .filter(filter => filter.type === 'DOCUMENT_META')
            .map(filter => {
                if (filter.allowedValues && filter.allowedValues.length > 0) {
                    return {
                        property: filter.property,
                        values: filter.allowedValues || [],
                        matchtype: 'MATCH',
                    };
                }
            })
            .filter(filter => filter !== undefined);
    }

    // Get filters that apply to document properties from filters config
    @computed
    get documentPropertyFilters() {
        return this.config.filtersConfig.filter(
            filter => filter.type === 'DOCUMENT_PROPERTY'
        );
    }

    // Builds a translation dictionary object for the current culture from all translation data. E.g:
    // { 'phrase': 'translation', 'phrase': 'translation', etc }
    @computed
    get translations() {
        const translationDictionary = {};

        this.translationData
            .filter(
                translation => translation.translationCulture === this.culture
            )
            .map(
                translation =>
                    (translationDictionary[translation.phrase] =
                        translation.translation)
            );

        return translationDictionary;
    }

    // Get sort matching fundGroupProperty
    @computed
    get fundGroupPropertySort() {
        return this.config.sort.find(
            s => s.key === this.config.fundGroupProperty
        );
    }

    @computed
    get hasAllFunds() {
        return this.funds.length >= this.total;
    }

    @computed
    get hideLoadMoreButton() {
        if (this.hasAllFunds) {
            const pagingConfig =
                this.config.explorer && this.config.explorer.paging;
            if (
                pagingConfig &&
                pagingConfig.hideLoadMoreButtonWhenAllRecordsRetrieved
            ) {
                return true;
            }
        }
        return false;
    }

    // Get disclaimers for header and footer as specified in config
    // Since the intended use is to display general purpose disclaimers not specifically for a fund,
    // we assume that disclaimers for the given types are not linked to an entity or segmentation list and do not have multiple disclaimers.
    @computed
    get headerDisclaimers() {
        return this.getUnlinkedDisclaimers(this.config.headerDisclaimerTypes);
    }

    @computed
    get footerDisclaimers() {
        return this.getUnlinkedDisclaimers(this.config.footerDisclaimerTypes);
    }

    @computed
    get fundListFilters(): Array<any> {
        const { fundListFilters } = this.config.selectedModule.config;
        if (fundListFilters) {
            return fundListFilters.map(config => {
                const { key, segmentationFilter } = config;
                return {
                    key,
                    fundList: this.getFundListsFromSegmentationFilter(
                        segmentationFilter
                    ),
                    fundListExclusions: this.getFundListExclusionsFromSegmentationFilter(
                        segmentationFilter
                    ),
                };
            });
        }
        return [];
    }

    @computed
    get fundLists(): string | Array<string> {
        const segmentationFilter = this.config.selectedModule.config
            .segmentationFilter;
        return this.getFundListsFromSegmentationFilter(segmentationFilter);
    }

    @computed
    get fundListExclusions(): Array<string> {
        const segmentationFilter = this.config.selectedModule.config
            .segmentationFilter;
        return this.getFundListExclusionsFromSegmentationFilter(
            segmentationFilter
        );
    }

    @computed
    get defaultShowChange() {
        let showChange = {
            showIcon: true,
            changeUpColor: 'inherit',
            changeDownColor: 'inherit',
        };

        if (
            this.config.selectedModule &&
            this.config.selectedModule.config &&
            this.config.selectedModule.config.explorer &&
            this.config.selectedModule.config.explorer.showChange
        ) {
            showChange = this.config.selectedModule.config.explorer.showChange;
        }

        return showChange;
    }

    @computed
    get linkFilter() {
        return this.fundLinkFilter;
    }

    configServices;
    services: Services;
    apiUrl;
    baseUrl;

    // App config
    @observable token = ''; // API token
    theme: any = {}; // App theme
    @observable config = {} as Config; // App config from config file
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection
    @observable columnStore = {} as ColumnStore; // Column processing and related functions

    // App loading states
    @observable isBootstrapped = false;
    @observable isExpanded = false;
    @observable isExpandAll = false;

    // Translation & culture
    @observable translationData = []; // Translation dictionary
    @observable defaultCulture = undefined; // this.config.defaultCulture; // Culture to use
    @observable cultures = []; // System cultures

    // Disclaimers
    @observable disclaimers = [];
    @observable disclaimersDictionary = {};

    // Search
    @observable fundlist = ''; // Segmentation list
    @observable identifierSearchValue = ''; // Search term
    @observable total = 0; // Search total
    @observable start = 0; // Search start
    @observable funds = []; // Search data
    @observable fundsForCsv = [];

    // Fund Detail Lists
    @observable fundDetailLists: FundDetailLists;

    // Filters
    @observable filters = {}; // Filter codes and values
    @observable activeFilters = {}; // Filter codes and values returned by getFilters with search
    @observable filterSearch = []; // Selected entity filters
    @observable filterParams = {}; // Filters from query string parameters
    @observable documentFilterSearch = []; // Selected document meta filters
    @observable documentPropertySearch = []; // Selected document property filters

    // Watchlist
    @observable watchList = [];
    @observable watchListActive = false;

    // Sorting
    @observable propertySort = [];
    @observable advancedSort;

    // Map of column index to selected column group index
    selectedGroupMap = observable.map();

    // Class Properties
    @observable classPropertiesByCode = {};

    // Global documents
    @observable globalDocuments = [];

    // Documents module
    @observable documentModule: DocumentModuleStore;

    @observable getFundsRunning: boolean = false;

    @observable getFundDetailListsRunning: boolean = false;

    @observable appDataProps: AppParams = {}; // data properties of root element

    @observable isMobile: boolean = false;

    @observable breakpointSize: BreakpointSize;

    @observable fundLinkFilter = {};

    debounceTimeout: NodeJS.Timeout;

    @observable expandStatus: ExpandStatus = {};

    constructor(stores: Stores, props) {
        super(stores);
        // Call init function with props passed from the main app component
        // Props will be the attributes from the element where the app is mounted
        this.init(props);

        // Search called in reaction to changes in search term
        // This is debounced to allow the user to finish typing
        reaction(
            () => this.identifierSearchValue,
            identifierSearchValue => {
                if (this.debounceTimeout !== undefined) {
                    clearTimeout(this.debounceTimeout);
                }
                this.debounceTimeout = setTimeout(() => {
                    if (this.config.isDocumentModule) {
                        if (this.documentModule) {
                            this.documentModule.getDocuments();
                        }
                    } else {
                        this.getFunds();
                        this.getFundDetailLists();
                    }
                }, this.config.searchMillisecondTimeout);
            }
        );

        // Search and getActiveFilters in reaction to:
        // entity filter selections
        // watchList toggle
        reaction(
            () => [
                this.filterSearch,
                this.documentFilterSearch,
                this.watchListActive,
            ],
            filters => {
                if (this.isBootstrapped) {
                    if (this.config.isDocumentModule) {
                        if (this.documentModule) {
                            this.documentModule.getDocuments();
                        }
                    } else {
                        this.getFunds();
                        this.getFundDetailLists();
                        this.getActiveFilters();
                    }
                }
            }
        );

        // Save watchList to local storage
        reaction(
            () => this.watchList,
            watchList => {
                localStorage.setItem(
                    this.watchListKey,
                    JSON.stringify(watchList)
                );
            }
        );

        // Reaction to change in module/tab gets funds and filters for new config
        reaction(
            () => this.config.currentModuleId,
            currentModuleId => {
                if (this.isBootstrapped) {
                    // explicit false so that default preserveExpandedRow is true
                    if (
                        this.config.selectedFundType.preserveExpandedRow ===
                        false
                    ) {
                        this.expandStatus = {};
                    }
                    if (this.config.isDocumentModule) {
                        this.documentModule = new DocumentModuleStore(this);
                        this.getDocumentFilters();
                    } else {
                        this.getWatchList();
                        this.setInitialSort();
                        this.getFunds();
                        this.getFundDetailLists();
                        this.getFilters();
                        this.getActiveFilters();
                        this.getDocumentFilters();
                        this.getGlobalDocuments();
                        this.getClassProperties();
                    }
                }
            }
        );

        // Reaction to change in fund type clears filters and gets filters for new config
        reaction(
            () => this.config.currentFundType,
            currentFundType => {
                if (this.isBootstrapped) {
                    this.expandStatus = {};
                    this.getWatchList();
                    this.clearFilters();
                    this.getFilters();
                    this.getDocumentFilters();
                    this.getGlobalDocuments();
                    this.getClassProperties();
                }
            }
        );
    }

    // Init function to boot the app
    @action
    init = async props => {
        const loadingId = this.stores.loading.start('global');
        this.getParams(props);

        if (!isNullOrUndefined(props.root)) {
            this.appDataProps = this.getAppParams(props.root);
        }

        this.getApiUrl(props.scriptSrc);

        this.configServices = new ConfigServices(this.apiUrl);
        this.columnStore = new ColumnStore(this);

        await this.getApplicationAppConfig();

        this.services = new Services(this.apiUrl, this.token);

        await this.getConfigFile();

        this.getWatchList();
        this.setInitialSort();
        this.addFilterSearch();

        Promise.all([
            this.getDisclaimers(),
            this.getTranslations(),
            this.getCultures(),
            this.getFilters(),
            this.getDocumentFilters(),
            this.getCultureFilters(),
            this.getGlobalDocuments(),
            this.getClassProperties(),
        ]);

        this.isBootstrapped = true;

        if (this.config.isDocumentModule) {
            this.documentModule = new DocumentModuleStore(this);
        } else {
            Promise.all([
                this.getFunds(),
                this.getFundDetailLists(),
                this.getActiveFilters(),
            ]);
        }

        // If Intl not supported then add locale data
        if (!props.intlSupported) {
            await this.addLocaleData();
        }

        this.stores.loading.stop('global', loadingId);
    };

    // Sets various values from props or query string parameters
    // Query string parameters take precedence over props
    @action
    getParams = props => {
        const {
            fundlist = props.fundlist,
            culture = props.culture,
            tab = props.tab,
            categoryTab = props.categorytab,
        } = getParams(location.search) as Params;

        this.filterParams = getFilterParams(location.search);
        this.isExpanded = this.filterParams.hasOwnProperty(EXPANDED_PARAM);
        this.isExpandAll = this.filterParams.hasOwnProperty(EXPAND_ALL_PARAM);
        this.fundlist = fundlist || '';
        this.defaultCulture = culture;
        this.currentModuleId = tab;
        this.currentFundType = categoryTab;
    };

    // get data properties off root element
    @action
    getAppParams(rootElement) {
        const appParamsFromElement = Object.keys(rootElement.attributes).reduce(
            (existing, key) => {
                let name = snakeCase(rootElement.attributes[key].name);
                // clean data prefix
                if (name.substring(0, 5).toLowerCase() === 'data_') {
                    name = name.substring(5, name.length);
                }
                existing[name] =
                    (rootElement &&
                        rootElement.attributes[key] &&
                        rootElement.attributes[key].value) ||
                    null;
                return existing;
            },
            {}
        ) as AppParams;

        return { ...appParamsFromElement };
    }

    // Parses an api url from the app script tag src... but why?!
    // Our services need to make api calls via the clients app instance url, which will be different for each client app.
    // We can't store it in the app config because we would need to know the app url to get it...
    // However, the script tag for the app will use the app url for the main javascript file.
    // E.g. https://api.fundpress.io/applicationManager/apps/xxxx-xxxx-xxxx-xxxx/main.js
    // So we pass the src of our own script tag into the app where we can get parse the app instance url.
    // We can then pass this to our services classes so api calls can hit the app url.
    @action
    getApiUrl = scriptSrc => {
        let apiUrl = scriptSrc
            .split('/')
            .slice(0, 6)
            .join('/');
        const baseUrl = scriptSrc
            .split('/')
            .slice(0, 3)
            .join('/');

        if (!apiUrl.includes('applicationManager/apps')) {
            apiUrl = '';
        }

        this.apiUrl = apiUrl;
        this.baseUrl = baseUrl;
    };

    // Gets application config for a token and theme
    @action
    getApplicationAppConfig = async () => {
        try {
            const config = await this.configServices.getApplicationAppConfig();
            this.theme = mergeAppStylesWithTheme(
                config.applicationStyles,
                theme
            );
            this.token = get(config, 'applicationConfiguration.apiToken');
        } catch (e) {
            console.warn(e);
        }
    };

    // Gets the config file
    @action
    getConfigFile = async () => {
        try {
            const configFile = await this.configServices.getConfigFile();
            this.config = new ConfigStore(
                this.stores,
                configFile,
                this.currentModuleId,
                this.currentFundType,
                this.fundlist,
                this.appDataProps
            );
        } catch (e) {
            console.warn(e);
        }
    };

    // Gets an Intl polyfill locale data file, e.g: fr-FR.json
    // then makes local data available to polyfilled Intl
    @action
    addLocaleData = async () => {
        try {
            const localeData = await this.configServices.getLocaleFile(
                this.culture
            );
            (window as any).Intl.__addLocaleData(localeData);
        } catch (e) {
            console.warn(e);
        }
    };

    // Updates filter search and document filter search based on filter params from query string.
    // If a filter property matches config.documentGroupMeta (e.g. document_type) then we can add a document filter search.
    // Ideally, _prefixed query string parameters would be reserved for filters,
    // but _expanded was already specified to a client, so we now have to handle it.
    @action
    addFilterSearch = () => {
        Object.keys(this.filterParams).map(property => {
            if (!DISALLOWED_FILTER_PARAMS.includes(property)) {
                const values = this.filterParams[property]
                    ? this.filterParams[property].split('|')
                    : [''];

                if (property === this.config.documentGroupMeta) {
                    this.documentFilterSearch.push({
                        property,
                        values,
                        matchtype: 'MATCH',
                    });
                } else {
                    this.filterSearch.push({
                        property,
                        values,
                        matchtype: 'MATCH',
                    });
                }
            }
        });
    };

    // Add configured advancedSort
    // Reset property sort
    @action
    setInitialSort = () => {
        this.advancedSort = this.config.advancedSort;
        this.propertySort = [];
    };

    @action
    getWatchList = () => {
        this.watchList =
            JSON.parse(localStorage.getItem(this.watchListKey)) || [];
    };

    @action
    toggleWatchList = () => {
        this.watchListActive = !this.watchListActive;
    };

    // Toggle sort
    @action
    toggleSort = column => {
        column = getSortableColumn(column);
        const property =
            get(column, 'data.property') || get(column, 'property');

        if (Object.keys(ADVANCED_SORT_TYPES).includes(property)) {
            this.toggleAdvancedSort(column);
        } else {
            this.togglePropertySort(column);
        }
    };

    // Toggle property sort
    @action
    togglePropertySort = column => {
        // Get sort key
        const key = get(column, 'property') || get(column, 'data.code');

        // Find the selected column in propertySort
        const existingColumn = this.propertySort.find(s => s.key === key);

        // If there is an existing sort, toggle the direction
        // If there is not an existing sort, replace with new sort
        if (existingColumn) {
            existingColumn.direction =
                existingColumn.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : SORT_DIRECTIONS.ASC;

            this.propertySort = [existingColumn];
        } else {
            this.propertySort = [
                {
                    key,
                    direction: SORT_DIRECTIONS.ASC,
                },
            ];
        }

        // If a sort is configured matching fundGroupProperty, add to top of sort array
        if (this.fundGroupPropertySort) {
            this.propertySort.unshift(this.fundGroupPropertySort);
        }

        // Clear advancedSort
        this.advancedSort = undefined;

        // Get data
        this.getFunds();
        this.getFundDetailLists();
    };

    // Toggle advanced sort
    @action
    toggleAdvancedSort = column => {
        // Build advanced search object
        const { property, code, key, sortKey = '' } = column.data;

        const advancedSort = {
            type: ADVANCED_SORT_TYPES[property],
            code,
            key: sortKey,
            value: key,
            direction: SORT_DIRECTIONS.ASC,
        };

        // See if we have already have matching advanced sort
        const existingSort = isExistingAdvancedSort(column, this.advancedSort);

        // If existing advanced sort, toggle the direction
        // If there is not an existing advanced sort, set it
        if (existingSort) {
            this.advancedSort.direction =
                this.advancedSort.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : SORT_DIRECTIONS.ASC;
        } else {
            this.advancedSort = advancedSort;
        }

        // Reset property sort
        this.propertySort = [];

        // Get data
        this.getFunds();
        this.getFundDetailLists();
    };

    // Get class property label from code
    @action
    getClassPropertyLabel = code => {
        const property = this.classPropertiesByCode[code];
        if (property) {
            return property.label;
        }
        return code;
    };

    // Get translations
    @action
    getTranslations = async () => {
        try {
            this.translationData = await this.services.getTranslation(
                this.config.defaultCulture
            );
        } catch (e) {
            console.warn(e);
        }
    };

    // Get fund data
    @action
    getFunds = async (paged = false) => {
        if (this.getFundsRunning) {
            return;
        }
        this.getFundsRunning = true;
        // Set start page
        this.start = paged ? this.start + this.config.limit : 0;

        // If loading initial results show main loading
        const loadingTarget: LoadingTarget = paged ? 'funds_more' : 'global';
        const loadingId = this.stores.loading.start(loadingTarget);

        // Remove filter searches not currently in filter properties
        // This ensures that only configured filters and query param filters are applied,
        // whilst retaining filter searches when switching between tabs
        const filterSearch = this.filterSearch.filter(s =>
            this.filterProperties.includes(s.property)
        );

        const search = this.watchListSearchEnabled
            ? filterSearch.concat(this.watchListSearch)
            : filterSearch.concat(this.fundTypeSearch);

        try {
            const { total, values } = await this.services.getData(
                this.fundLists,
                this.fundListExclusions,
                search,
                this.identifierSearch,
                this.propertySort.length > 0
                    ? this.propertySort
                    : this.config.sort,
                this.advancedSort,
                this.documentsJoinSpec,
                this.start,
                this.config.limit,
                this.culture,
                this.config.defaultCulture,
                this.config.cultureSearchTransform,
                this.config.timeseriesInclude,
                this.config.statisticsInclude,
                this.config.allocationsInclude
            );

            // If paged then join to existing data, otherwise set new data
            this.funds = paged ? this.funds.concat(values) : values;

            // Build link filter
            await this.buildLinkFilter();

            const computedTotal = paged
                ? Math.max(total, this.funds.length)
                : total;

            this.total = computedTotal;
        } catch (e) {
            console.warn(e);
        }
        this.getFundsRunning = false;
        this.stores.loading.stop(loadingTarget, loadingId);
    };

    // get fund detail lists from fund list filters
    @action
    getFundDetailLists = async () => {
        if (this.getFundDetailListsRunning) {
            return;
        }
        this.getFundDetailListsRunning = true;
        const fundDetailLists: any = {};
        const fundListFilters = this.fundListFilters;

        if (!utils.typeChecks.isNullOrEmpty(fundListFilters)) {
            // Remove filter searches not currently in filter properties
            // This ensures that only configured filters and query param filters are applied,
            // whilst retaining filter searches when switching between tabs
            const filterSearch = this.filterSearch.filter(s =>
                this.filterProperties.includes(s.property)
            );

            const search = this.watchListSearchEnabled
                ? filterSearch.concat(this.watchListSearch)
                : filterSearch.concat(this.fundTypeSearch);

            for (const {
                key,
                fundList,
                fundListExclusion,
            } of fundListFilters) {
                try {
                    const { values } = await this.services.getDataSimplified(
                        fundList,
                        fundListExclusion,
                        search,
                        this.identifierSearch,
                        {},
                        ['isin']
                    );

                    fundDetailLists[key] = values.map(value =>
                        this.getEntityProperty(value, 'isin')
                    );
                } catch (e) {
                    console.warn(e);
                }
            }
        }
        this.fundDetailLists = fundDetailLists;
        this.getFundDetailListsRunning = false;
    };

    @action
    buildLinkFilter = async () => {
        const fundLinkFilter = {};
        // get column config
        for (const column of this.config.selectedModule.config.columnConfig) {
            await this.setFundLinkFilter(
                column.url,
                column.key,
                fundLinkFilter
            );
        }

        // get row config
        const rowConfig = this.config.selectedModule.config
            .rowConfig as FundCardRowConfig;

        if (rowConfig && rowConfig.header && rowConfig.header.url) {
            await this.setFundLinkFilter(
                rowConfig.header.url,
                rowConfig.header.key,
                fundLinkFilter
            );
        }
        this.fundLinkFilter = fundLinkFilter;
    };

    @action
    setFundLinkFilter = async (
        urlConfig: string | LinkFilter,
        key: string,
        fundLinkFilter: any
    ) => {
        const url: string | LinkFilter = this.getUrl(urlConfig);
        if (url && (url as LinkFilter).linkSegmentationFilter) {
            const allowedValues: Array<any> = await this.getFilteredFunds(
                url as LinkFilter
            );
            fundLinkFilter[key] = allowedValues.map(value => value.clientCode);
        }
    };

    @action
    getUrl(configUrl: string | LinkFilter) {
        return this.config.selectedModule.config.links &&
            this.config.selectedModule.config.links[configUrl as string]
            ? this.config.selectedModule.config.links[configUrl as string]
            : (configUrl as LinkFilter);
    }

    @action
    getFilteredFunds = async (url: LinkFilter) => {
        const inclusionClauses = url.linkSegmentationFilter.inclusionClauses;
        const exclusionClauses = url.linkSegmentationFilter.exclusionClauses;

        if (!isNullOrUndefined(inclusionClauses)) {
            inclusionClauses.forEach((element, index) => {
                inclusionClauses[index] = replacePlaceholders({
                    text: element,
                    appParams: this.appDataProps,
                });
            });
        }

        if (!isNullOrUndefined(exclusionClauses)) {
            exclusionClauses.forEach((element, index) => {
                exclusionClauses[index] = replacePlaceholders({
                    text: element,
                    appParams: this.appDataProps,
                });
            });
        }

        const clientCodes = this.funds.map(fund => fund.clientCode);

        // Clean out params that are not needed. set includeProperties to empty array to remove properties_pub from the response.
        const { values: allowedValues } = await this.services.getData(
            inclusionClauses || [],
            exclusionClauses || [],
            [],
            undefined,
            undefined,
            undefined,
            undefined,
            0,
            0,
            undefined,
            undefined,
            this.config.cultureSearchTransform,
            undefined,
            undefined,
            undefined,
            clientCodes,
            {},
            false,
            false,
            false,
            []
        );
        return allowedValues;
    };

    handleDownloadDisclaimers(
        disclaimerType: ExplorerDownloadDisclaimerSection,
        moduleCollection: Array<any>
    ) {
        const response: Array<string> = [];
        if (disclaimerType) {
            const {
                useModuleDisclaimers: useModuleTypes,
                order,
                types,
            } = disclaimerType;
            // Default the order to show disclaimers from the module first
            if (useModuleTypes && (!order || order === 'moduleFirst')) {
                response.push(...moduleCollection);
            }
            if (types && Array.isArray(toJS(types))) {
                response.push(...this.getUnlinkedDisclaimers(types));
            }
            if (useModuleTypes && order === 'explicitFirst') {
                response.push(...moduleCollection);
            }
        }
        return response;
    }

    @action
    downloadTable = async () => {
        const explorerDownload =
            this.config.explorer && this.config.explorer.download;
        if (explorerDownload && explorerDownload.enabled) {
            const { disclaimerSections: disclaimerTypes } = explorerDownload;
            await this.getFundsForCsv();

            let headerDisclaimers = [];
            let footerDisclaimers = [];
            if (disclaimerTypes) {
                const { header, footer } = disclaimerTypes;
                headerDisclaimers = this.handleDownloadDisclaimers(
                    header,
                    this.headerDisclaimers
                );
                footerDisclaimers = this.handleDownloadDisclaimers(
                    footer,
                    this.footerDisclaimers
                );
            }
            const options = {
                headerDisclaimers,
                footerDisclaimers,
                dataStore: this,
                config: this.config,
                columnConfig: { columns: this.columnStore.columns },
                shareClasses: this.fundsForCsv,
                locale: this.culture,
                translations: this.translations,
            };
            const csv = CsvHelper.convertTableToCsv(options);
            const fileNameParts: Array<string> = [];
            const selectedFundType = this.config.selectedFundType;
            if (selectedFundType) {
                if (this.config.showFundTypes) {
                    fileNameParts.push(selectedFundType.label);
                }
                const selectedModule = this.config.selectedModule;
                if (selectedModule) {
                    fileNameParts.push(selectedModule.label);
                }
            } else {
                fileNameParts.push('Export');
            }
            const now = new Date();
            const year = now.getFullYear();
            const month = padStart(now.getMonth() + 1, 2);
            const day = padStart(now.getDate(), 2);

            fileNameParts.push(`${year}${month}${day}`);
            const fileName =
                fileNameParts
                    .map(part => translate(this.translations, part))
                    .join('_') + '.csv';

            this.downloadCsv(csv, fileName);
        }
    };

    @action
    getFundsForCsv = async () => {
        // If loading initial results show main loading
        const loadingId = this.stores.loading.start('global');

        // Remove filter searches not currently in filter properties
        // This ensures that only configured filters and query param filters are applied,
        // whilst retaining filter searches when switching between tabs
        const filterSearch = this.filterSearch.filter(s =>
            this.filterProperties.includes(s.property)
        );

        const search = this.watchListSearchEnabled
            ? filterSearch.concat(this.watchListSearch)
            : filterSearch.concat(this.fundTypeSearch);

        try {
            const { values } = await this.services.getData(
                this.fundLists,
                this.fundListExclusions,
                search,
                this.identifierSearch,
                this.propertySort.length > 0
                    ? this.propertySort
                    : this.config.sort,
                this.advancedSort,
                undefined,
                0,
                0,
                this.culture,
                this.config.defaultCulture,
                this.config.cultureSearchTransform,
                this.config.timeseriesInclude,
                this.config.statisticsInclude,
                this.config.allocationsInclude
            );

            // If paged then join to existing data, otherwise set new data
            this.fundsForCsv = values;
        } catch (e) {
            console.warn(e);
        }

        this.stores.loading.stop('global', loadingId);
    };

    // Set search term
    @action
    search = value => {
        this.identifierSearchValue = value;
    };

    // Clear search term
    @action
    clearSearch = () => {
        this.identifierSearchValue = '';
    };

    // Clear filters
    @action
    clearFilters = () => {
        this.filterSearch = [];
        this.documentFilterSearch = [];
        this.documentPropertySearch = [];
    };

    @action
    clearFiltersOfType = (
        property: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const updatedSearch = toJS(this[filterSearchKey]);
        this[filterSearchKey] = updatedSearch.filter(
            search => search.property !== property
        );
    };

    // Get values for specified properties limited by any allowed values specified in filter config
    @action
    getFilters = async () => {
        if (this.filterProperties.length > 0) {
            try {
                const filters = await this.services.getFilters(
                    this.filterProperties,
                    this.fundTypeSearch,
                    this.fundLists,
                    this.fundListExclusions
                );

                Object.keys(filters).forEach(filter => {
                    const { valueAliases = {}, allowedValues = [] } =
                        this.config.filtersConfig.find(
                            f => f.property === filter
                        ) || {};
                    filters[filter] = filters[filter]
                        .filter(f =>
                            allowedValues.length > 0
                                ? allowedValues.includes(f.value)
                                : true
                        )
                        .map(f => ({
                            label: get(valueAliases, f.label, f.label),
                            value: f.value,
                        }));
                });

                this.filters = { ...this.filters, ...filters };
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get values for specified properties limited by filter property selections
    @action
    getActiveFilters = async () => {
        if (this.filterProperties.length > 0) {
            const search = this.watchListSearchEnabled
                ? this.filterSearch.concat(this.watchListSearch)
                : this.filterSearch;

            try {
                this.activeFilters = await this.services.getFilters(
                    this.filterProperties,
                    search,
                    this.fundLists,
                    this.fundListExclusions
                );
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get values for specified document meta, limited by any allowed values specified in filter config
    // Label is replaced by value for consistency with fund/getFilters
    @action
    getDocumentFilters = async () => {
        if (this.documentFilterCodes.length > 0) {
            try {
                const filters = await this.services.getDocumentFilters(
                    this.documentFilterCodes,
                    this.documentFilterAllowedValuesSearch
                );
                Object.keys(filters).forEach(filter => {
                    const { valueAliases = {} } =
                        this.config.filtersConfig.find(
                            f => f.property === filter
                        ) || {};
                    const languagesAdded = [];
                    const labelAliases = [];
                    for (const f of filters[filter]) {
                        const values = isArray(f.value) ? f.value : [f.value];
                        const labels = f.label
                            .replace(/\[|"|\]/g, '')
                            .split(',');
                        values.map((value, idx) => {
                            if (languagesAdded.indexOf(value) === -1) {
                                languagesAdded.push(value);
                                labelAliases.push({
                                    label: get(
                                        valueAliases,
                                        labels[idx],
                                        labels[idx]
                                    ),
                                    value,
                                });
                            }
                        });
                    }
                    filters[filter] = labelAliases;
                });
                this.filters = { ...this.filters, ...filters };
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get property metadata
    @action
    getClassProperties = async () => {
        if (!isNullOrUndefined(this.classPropertiesByCode)) {
            try {
                const values = await this.services.getProperties('CLSS');
                this.classPropertiesByCode = {};
                for (const value of values) {
                    this.classPropertiesByCode[value.code] = value;
                }
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get global documents as per globalDocumentSearch
    @action
    getGlobalDocuments = async () => {
        this.globalDocuments = [];

        if (this.config.globalDocumentSearch.length > 0) {
            try {
                const { values } = await this.services.getDocuments({
                    search: this.config.globalDocumentSearch,
                });
                this.globalDocuments = values;
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get system cultures
    @action
    getCultures = async () => {
        try {
            this.cultures = await this.services.getCultures();
        } catch (e) {
            console.warn(e);
        }
    };

    // Add cultureCode to filters in the same format as returned by getFilters for entities and documents.
    // cultureCode: [{ label: 'English', value: 'en-GB' }]
    // This enables us to handle selections in the same way as other filters.
    // Also alias labels as defined in config.cultureAliases (takes precedence) or filter.valueAliases
    @action
    getCultureFilters = async () => {
        const { allowedValues = [], valueAliases = {} } =
            this.documentPropertyFilters.find(
                filter => filter.property === 'cultureCode'
            ) || {};
        const cultureFilters = {
            cultureCode: this.cultures
                .filter(culture =>
                    allowedValues.length > 0
                        ? allowedValues.includes(culture.culture)
                        : true
                )
                .map(culture => ({
                    label:
                        get(this.config.cultureAliases, culture.culture) ||
                        get(valueAliases, culture.culture, culture.culture),
                    value: culture.culture,
                })),
        };
        this.filters = { ...this.filters, ...cultureFilters };
    };

    getDocumentDisplayName(document: any) {
        if (this.config.documentDisplayName) {
            const dataFields = this.config.documentDisplayName.dataFields;
            const values = Object.keys(dataFields || {}).reduce((acc, key) => {
                const datafield = dataFields[key];
                const { property, format } = datafield;
                acc[key] =
                    get(document, property) ||
                    get(document.meta, `${property}.value`, '');
                if (acc[key] && format) {
                    acc[key] = formatter({
                        dataStore: this,
                        type: format.type,
                        locale: this.culture,
                        currency: '',
                        options: format.options,
                        value: acc[key],
                    });
                }
                return acc;
            }, {});
            return replacePlaceholders({
                values,
                text: this.config.documentDisplayName.text,
                appParams: this.appDataProps,
            });
        }

        return get(
            this.config.cultureAliases,
            document.cultureCode,
            document.cultureCode
        );
    }

    // Download documents zip with document clientCodes
    @action
    downloadDocuments = async (documents: Array<any> = []) => {
        if (documents.length > 0) {
            const clientCodes = documents.map(document => document.clientCode);
            const loadingId = this.stores.loading.start('global');

            const downloadZip = clientCodes.length > 1 ? true : false;
            let parsedFilename = clientCodes[0];
            if (this.config.documentFilename) {
                const dataFields = this.config.documentFilename.dataFields;
                const data = Object.keys(dataFields || {}).reduce(
                    (acc, key) => {
                        const datafield = dataFields[key];
                        const { property, format } = datafield;
                        acc[key] = get(
                            documents[0].meta,
                            `${property}.value`,
                            ''
                        );
                        if (acc[key] && format) {
                            acc[key] = formatter({
                                dataStore: this,
                                type: format.type,
                                locale: this.culture,
                                currency: '',
                                options: format.options,
                                value: acc[key],
                            });
                        }
                        return acc;
                    },
                    {}
                );
                parsedFilename = replacePlaceholders({
                    values: data,
                    text: this.config.documentFilename.text,
                    appParams: this.appDataProps,
                });
            }

            const filename = downloadZip ? 'documents' : parsedFilename;

            try {
                const documentData = downloadZip
                    ? await this.services.documentDownloadZip(clientCodes)
                    : await this.services.documentDownload(clientCodes);

                const extension = getExtension(documentData.type);

                FileSaver.saveAs(
                    documentData,
                    extension ? `${filename}.${extension}` : filename
                );
            } catch (e) {
                console.warn(e);
            }

            this.stores.loading.stop('global', loadingId);
        }
    };

    // Download CSV file from CSV string
    @action
    downloadCsv = async (csv: string, filename: string) => {
        const loadingId = this.stores.loading.start('global');

        try {
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

            FileSaver.saveAs(blob, filename);
        } catch (e) {
            console.warn(e);
        }

        this.stores.loading.stop('global', loadingId);
    };

    // Get disclaimers
    @action
    getDisclaimers = async () => {
        try {
            this.disclaimers = await this.services.getDisclaimers();
            const disclaimers = groupBy(this.disclaimers, 'disclaimerType');

            for (const d in disclaimers) {
                if (d) {
                    const disclaimer =
                        disclaimers[d][0].disclaimers.find(
                            disclaimerContent =>
                                disclaimerContent.culture === this.culture
                        ) ||
                        disclaimers[d][0].disclaimers.find(
                            disclaimerContent =>
                                disclaimerContent.culture ===
                                this.config.defaultCulture
                        );

                    disclaimers[d] = get(disclaimer, 'disclaimer', '');
                }
            }

            this.disclaimersDictionary = disclaimers;
        } catch (e) {
            console.warn(e);
        }
    };

    getUnlinkedDisclaimers = (disclaimerTypes = []) => {
        const disclaimers = disclaimerTypes
            .map(d => get(this.disclaimersDictionary, d))
            .filter(d => d !== undefined);

        return disclaimers;
    };

    @action
    setIsMobile = value => (this.isMobile = value);

    @action
    setBreakpointSize = value =>
        this.breakpointSize || (this.breakpointSize = value);

    getFundListsFromSegmentationFilter(
        segmentationFilter: SegmentationFilter
    ): string | Array<string> {
        const fundList = [];
        if (segmentationFilter) {
            if (
                segmentationFilter.inclusionClauses &&
                segmentationFilter.inclusionClauses.length > 0
            ) {
                fundList.push(...segmentationFilter.inclusionClauses);
            }
        }

        if (!isEmpty(this.fundlist)) {
            fundList.push(this.fundlist);
        }

        fundList.forEach((element, index) => {
            fundList[index] = replacePlaceholders({
                text: element,
                appParams: this.appDataProps,
            });
        });

        // API doesn't work correctly passing an empty array, default to empty string
        return fundList.length > 0 ? fundList : '';
    }

    getFundListExclusionsFromSegmentationFilter(
        segmentationFilter: SegmentationFilter
    ): Array<string> {
        const fundListExclusion = [];
        if (segmentationFilter) {
            if (
                segmentationFilter.exclusionClauses &&
                segmentationFilter.exclusionClauses.length > 0
            ) {
                fundListExclusion.push(...segmentationFilter.exclusionClauses);
            }
        }
        fundListExclusion.forEach((element, index) => {
            fundListExclusion[index] = replacePlaceholders({
                text: element,
                appParams: this.appDataProps,
            });
        });
        return fundListExclusion;
    }

    getEntityProperty(entity: IEntityEntity, property: string) {
        if (entity && entity.properties_pub) {
            const prop = entity.properties_pub[property];
            return prop.value || prop;
        }
        return entity;
    }

    fundListContainsEntity(entity: IEntityEntity, key: string): boolean {
        const isin = this.getEntityProperty(entity, 'isin');
        return (
            this.fundDetailLists &&
            this.fundDetailLists[key] &&
            this.fundDetailLists[key].indexOf(isin) >= 0
        );
    }

    @action
    setExpandStatus(entity: IEntity, expanded?: boolean) {
        const { type, clientCode } = entity;
        if (!this.expandStatus[type]) {
            this.expandStatus[type] = {};
        }
        if (expanded !== undefined) {
            this.expandStatus[type][clientCode] = expanded;
        }
        this.expandStatus[type][clientCode] = !this.getExpandStatus(entity);
        // trigger computed refresh
        this.expandStatus = { ...this.expandStatus };
    }

    getExpandStatus(entity: IEntity): boolean {
        const { type, clientCode } = entity;
        const expandStatusValue =
            this.expandStatus &&
            this.expandStatus[type] &&
            this.expandStatus[type][clientCode];

        return expandStatusValue !== undefined
            ? expandStatusValue
            : this.isExpanded;
    }
}



// WEBPACK FOOTER //
// ./src/stores/DataStore.ts